import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import useMultiSelectionLayout from '../../../hooks/useMultiSelectionLayout';
import {
  MULTI_SELECTION_ALIGN,
  MULTI_SELECTION_DISTRIBUTE,
} from '../../../utils/computeMultiSelectionLayoutUpdates';
import { useStore } from '../../../hooks';
import SidebarPanelButton from '../SidebarPanelButton';
import {
  AlignBottom,
  AlignCenter,
  AlignLeft,
  AlignMiddle,
  AlignRight,
  AlignTop,
  DistributeHorizontal,
  DistributeVertical,
} from '../../../utils/editorReactIcons';

const LAYOUT_ACTIONS = [
  {
    id: MULTI_SELECTION_ALIGN.LEFT,
    type: 'align',
    titleKey: 'multiAlignLeftTitle',
    labelKey: 'multiAlignLeftShort',
    Icon: AlignLeft,
  },
  {
    id: MULTI_SELECTION_ALIGN.CENTER,
    type: 'align',
    titleKey: 'multiAlignCenterTitle',
    labelKey: 'multiAlignCenterShort',
    Icon: AlignCenter,
  },
  {
    id: MULTI_SELECTION_ALIGN.RIGHT,
    type: 'align',
    titleKey: 'multiAlignRightTitle',
    labelKey: 'multiAlignRightShort',
    Icon: AlignRight,
  },
  {
    id: MULTI_SELECTION_ALIGN.TOP,
    type: 'align',
    titleKey: 'multiAlignTopTitle',
    labelKey: 'multiAlignTopShort',
    Icon: AlignTop,
  },
  {
    id: MULTI_SELECTION_ALIGN.MIDDLE,
    type: 'align',
    titleKey: 'multiAlignMiddleTitle',
    labelKey: 'multiAlignMiddleShort',
    Icon: AlignMiddle,
  },
  {
    id: MULTI_SELECTION_ALIGN.BOTTOM,
    type: 'align',
    titleKey: 'multiAlignBottomTitle',
    labelKey: 'multiAlignBottomShort',
    Icon: AlignBottom,
  },
  {
    id: MULTI_SELECTION_DISTRIBUTE.HORIZONTAL,
    type: 'distribute',
    titleKey: 'multiDistributeHorizontalTitle',
    labelKey: 'multiDistributeHorizontalShort',
    Icon: DistributeHorizontal,
  },
  {
    id: MULTI_SELECTION_DISTRIBUTE.VERTICAL,
    type: 'distribute',
    titleKey: 'multiDistributeVerticalTitle',
    labelKey: 'multiDistributeVerticalShort',
    Icon: DistributeVertical,
  },
];

const MULTI_SELECTION_LAYOUT_ROW_GROUPS = [
  [MULTI_SELECTION_ALIGN.LEFT, MULTI_SELECTION_ALIGN.RIGHT],
  [MULTI_SELECTION_ALIGN.CENTER, MULTI_SELECTION_ALIGN.MIDDLE],
  [MULTI_SELECTION_ALIGN.TOP, MULTI_SELECTION_ALIGN.BOTTOM],
  [
    MULTI_SELECTION_DISTRIBUTE.HORIZONTAL,
    MULTI_SELECTION_DISTRIBUTE.VERTICAL,
  ],
];

const MultiSelectionAlignControls = ({ variant = 'sidebar' }) => {
  const { t } = useStore();
  const { align, distribute, canAlign, canDistribute } = useMultiSelectionLayout();

  const alignDisabledHint = t('multiAlignDisabledNeedTwo');
  const distributeDisabledHint = t('multiDistributeDisabledNeedThree');

  const getActionTitle = useCallback(
    (action) => {
      const base = t(action.titleKey);
      if (action.type === 'distribute') {
        return canDistribute ? base : `${base} — ${distributeDisabledHint}`;
      }
      return canAlign ? base : `${base} — ${alignDisabledHint}`;
    },
    [alignDisabledHint, canAlign, canDistribute, distributeDisabledHint, t],
  );

  const handleAction = useCallback(
    (action) => {
      if (action.type === 'distribute') {
        if (canDistribute) {
          distribute(action.id);
        }
        return;
      }
      if (canAlign) {
        align(action.id);
      }
    },
    [align, canAlign, canDistribute, distribute],
  );

  const handleKeyDown = useCallback(
    (event, action, disabled) => {
      if (disabled) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleAction(action);
      }
    },
    [handleAction],
  );

  const actionsById = useMemo(
    () =>
      LAYOUT_ACTIONS.reduce((acc, action) => {
        acc[action.id] = action;
        return acc;
      }, {}),
    [],
  );

  const renderActionButton = useCallback(
    (action) => {
      const disabled =
        action.type === 'distribute' ? !canDistribute : !canAlign;
      const title = getActionTitle(action);
      const classSuffix =
        action.type === 'distribute'
          ? `distribute-${action.id}`
          : action.id;

      return (
        <SidebarPanelButton
          className={`respark_multi-layout-btn respark_multi-layout-btn--${classSuffix}`}
          Icon={action.Icon}
          label={t(action.labelKey)}
          title={title}
          disabled={disabled}
          onClick={() => handleAction(action)}
          onKeyDown={(event) => handleKeyDown(event, action, disabled)}
        />
      );
    },
    [
      canAlign,
      canDistribute,
      getActionTitle,
      handleAction,
      handleKeyDown,
      t,
    ],
  );

  if (variant !== 'sidebar') {
    return null;
  }

  return (
    <div
      className="respark_multi-selection-layout-grid"
      role="group"
      aria-label={t('multiAlignTitle')}
    >
      {MULTI_SELECTION_LAYOUT_ROW_GROUPS.map((rowIds) => (
        <div
          key={rowIds.join('-')}
          className="respark_inspector-option-row respark_multi-selection-layout-row"
        >
          {rowIds.map((actionId) => {
            const action = actionsById[actionId];
            if (!action) {
              return null;
            }

            return (
              <div
                key={action.id}
                className="respark_inspector-option-row-cell"
              >
                {renderActionButton(action)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

MultiSelectionAlignControls.propTypes = {
  variant: PropTypes.oneOf(['sidebar']),
  className: PropTypes.string,
};

export default MultiSelectionAlignControls;
